import { Link } from "react-router";
import { Alerta, BotonWhatsapp, Button, DataList, Dialog, Row, Spinner, Stack, TextField } from "@/components";
import type { CompradorListado } from "@/lib/api";
import { formatTelefono } from "@/lib/telefonos";
import { ETIQUETA_ESTADO_VENTA, ETIQUETA_TIPO_COMPRADOR, nombreUnidades } from "@/lib/ventas";
import { FormularioComprador } from "./FormularioComprador";
import { useCompradoresTab } from "./useCompradoresTab";

function Dato({ etiqueta, valor }: { etiqueta: string; valor: string | null }) {
  return (
    <div className="contents">
      <dt className="text-texto-suave">{etiqueta}</dt>
      <dd className="break-words">{valor ?? "—"}</dd>
    </div>
  );
}

function contacto(c: CompradorListado): string {
  return [c.email, c.telefono && formatTelefono(c.telefono)].filter(Boolean).join(" · ") || "—";
}

export function CompradoresTab() {
  const t = useCompradoresTab();
  const d = t.dialogo;
  const f = t.ficha;
  const completo = f?.comprador ?? null;

  const columnas = [
    { clave: "nombre", titulo: "Nombre", render: (c: CompradorListado) => c.nombre },
    { clave: "documento", titulo: "Documento", render: (c: CompradorListado) => c.documento ?? "—" },
    { clave: "contacto", titulo: "Contacto", render: contacto },
    { clave: "ventas", titulo: "Ventas", render: (c: CompradorListado) => String(c.ventas_cantidad) },
  ];

  const acciones = (c: CompradorListado) => (
    <>
      <Button variante="secundario" tamanio="sm" aria-label={`Ver ${c.nombre}`} onClick={() => t.verFicha(c.id)}>
        Ver
      </Button>
      {t.esAdmin && (
        <Button variante="fantasma" tamanio="sm" aria-label={`Borrar ${c.nombre}`} onClick={() => t.abrirBorrar(c)}>
          Borrar
        </Button>
      )}
      <BotonWhatsapp telefono={c.telefono} nombre={c.nombre} />
    </>
  );

  return (
    <Stack espacio={6}>
      <Stack espacio={2}>
        <Link to="/ventas" className="text-chico text-primario underline-offset-2 hover:underline">
          ← Ventas
        </Link>
        <Row justificar="entre" envolver espacio={3}>
          <h1 className="text-pantalla font-light tracking-[-0.03em]">Compradores</h1>
          {t.esAdmin && <Button onClick={t.abrirNuevo}>Nuevo comprador</Button>}
        </Row>
      </Stack>

      <TextField etiqueta="Buscar" placeholder="Nombre" type="search" maxLength={120} value={t.q} onChange={(e) => t.setQ(e.target.value)} contenedorClassName="max-w-sm" />

      {t.cargando && (
        <div className="grid place-items-center py-10 text-primario">
          <Spinner />
        </div>
      )}
      {t.errorCarga && (
        <Stack espacio={3}>
          <Alerta texto={t.errorCarga} />
          <div>
            <Button variante="secundario" onClick={t.reintentar}>
              Reintentar
            </Button>
          </div>
        </Stack>
      )}
      {!t.cargando && !t.errorCarga && (
        <Stack espacio={3}>
          <Alerta texto={t.errorBusqueda} />
          <DataList etiqueta="Compradores" columnas={columnas} filas={t.items} claveFila={(c) => c.id} acciones={acciones} vacio={t.q.trim() ? "Ningún comprador coincide con la búsqueda." : "Todavía no hay compradores."} />
        </Stack>
      )}

      <Dialog abierto={d?.tipo === "ficha"} onCambio={(abierto) => !abierto && t.cerrar()} titulo={f?.comprador.nombre ?? "Comprador"}>
        {t.errorFicha ? (
          <Alerta texto={t.errorFicha} />
        ) : !f ? (
          <div className="grid place-items-center py-6 text-primario">
            <Spinner />
          </div>
        ) : (
          <Stack espacio={4}>
            {completo && (
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-chico">
                <Dato etiqueta="Tipo" valor={ETIQUETA_TIPO_COMPRADOR[completo.tipo]} />
                <Dato etiqueta="Documento" valor={completo.documento} />
                <Dato etiqueta="Email" valor={completo.email} />
                <Dato etiqueta="Teléfono" valor={completo.telefono && formatTelefono(completo.telefono)} />
                <Dato etiqueta="Domicilio" valor={completo.domicilio} />
                <Dato etiqueta="Notas" valor={completo.notas} />
              </dl>
            )}
            <Stack espacio={2}>
              <h3 className="text-chico font-medium text-texto-suave">Ventas</h3>
              {f.ventas.length === 0 ? (
                <p className="text-chico text-texto-suave">Todavía no tiene ventas.</p>
              ) : (
                <ul className="flex flex-col gap-1">
                  {f.ventas.map((v) => (
                    <li key={v.id}>
                      <Link to={`/ventas/${v.id}`} className="text-primario underline-offset-2 hover:underline">
                        Venta {v.numero} · {ETIQUETA_ESTADO_VENTA[v.estado]} · {nombreUnidades(v.unidades)}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Stack>
            {t.esAdmin && completo && (
              <Row justificar="fin">
                <Button variante="secundario" onClick={() => t.abrirEditar(completo)}>
                  Editar
                </Button>
              </Row>
            )}
          </Stack>
        )}
      </Dialog>

      <Dialog abierto={d?.tipo === "form"} onCambio={(abierto) => !abierto && t.cerrar()} titulo={d?.tipo === "form" && d.comprador ? "Editar comprador" : "Nuevo comprador"}>
        <FormularioComprador form={t.form} cambiar={t.cambiar} errores={t.errores} onSubmit={(e) => void t.guardar(e)} onCancelar={t.cerrar} guardando={t.guardando} error={t.errorGuardar} textoGuardar="Guardar" />
      </Dialog>

      <Dialog abierto={d?.tipo === "borrar"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Borrar comprador">
        {d?.tipo === "borrar" && (
          <Stack espacio={4}>
            <p>¿Borrar a {d.comprador.nombre}? Si tiene ventas cargadas, no se puede borrar.</p>
            <Alerta texto={t.errorBorrar} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={t.cerrar}>
                Cancelar
              </Button>
              <Button variante="peligro" cargando={t.borrando} onClick={() => void t.confirmarBorrar()}>
                Borrar
              </Button>
            </Row>
          </Stack>
        )}
      </Dialog>
    </Stack>
  );
}
